/* <product-form-controller> — variant + selling-plan state for the main PDP
   form. It never POSTs: cart.js's <product-form> owns the submit; this
   controller only keeps the form inputs and price display truthful.
   Markup contract:
   - ONE <script type="application/json" data-product-json> INSIDE this
     element (data only, not executable); missing = log once + stay inert,
     never adopt another section's data
   - [data-section-id] on the host — stamped onto bus events
   - [data-main-atc] form[action*="/cart/add"] with input[name="id"] — the
     controller binds to the MAIN form only, never an upsell block's mini
     form placed above the buy box (#263, 2026-09-09, Codex F07); a bare
     form[action*="/cart/add"] is the fallback when no marker exists
   - [data-option-input]  option radios/selects grouped by name (or
     data-option-name), ordered by data-option-position (1-based) when
     present. CHECKED state wins on load; the hidden id only decides when
     no option inputs exist.
   - [data-selling-plan]  server-rendered (Skio) plan radios named
     "selling_plan"; value="" = one-time. The checked radio IS the form
     field CartAPI reads — no hidden mirror input (#262, 2026-09-09, Codex
     F05: the mirror started blank until this controller ran, so a pre-JS
     "Subscribe" click posted a one-time purchase). Plans not allocated to
     the current variant hide (their <label>); an orphaned selection falls
     back to one-time, or the first allocated plan under
     requires_selling_plan.
   - display targets (all inside): [data-price], [data-compare-price],
     [data-plan-price][data-plan-id] per-plan price labels (non-inputs),
     [data-availability] and submit label span [data-atc-text] with
     data-in-stock/data-add/data-sold-out/data-unavailable strings
   Emits bus 'variant:changed' on every option AND plan change:
   { sectionId, productId, variant, planId, unitPrice, comparePrice,
     available } — plan-adjusted cents, planId null = one-time, variant null
   for impossible combinations; consumers filter on their own sectionId.
   */

import { bus, formatMoney } from './critical.js';

let warned = false;

class ProductFormController extends HTMLElement {
  connectedCallback() {
    const json = this.querySelector('[data-product-json]');
    if (!json) {
      if (!warned) console.warn('product-form: missing data-product-json');
      warned = true;
      return;
    }
    try {
      this.product = JSON.parse(json.textContent);
    } catch {
      return;
    }
    if (!Array.isArray(this.product?.variants)) return;

    /* #263 (2026-09-09, Codex F07): bind to the MAIN form. This element wraps
       every buy-box block, so the first form in DOM order can be an upsell
       block's mini form placed above the buy box — the variant id, plan and
       submit label would land on the wrong form. */
    this.form =
      this.querySelector('[data-main-atc] form[action*="/cart/add"]') ||
      this.querySelector('form[action*="/cart/add"]');
    this.idInput = this.form ? this.form.querySelector('input[name="id"]') : null;

    for (const input of this.querySelectorAll('[data-option-input]')) {
      input.addEventListener('change', () => this.onOptionChange());
    }

    /* #262 (2026-09-09, Codex F05): the plan radios are name="selling_plan"
       themselves — nothing to create or keep in sync. */
    for (const radio of this.querySelectorAll('[data-selling-plan]')) {
      radio.addEventListener('change', () => {
        this.render();
        this.emitChange();
      });
    }

    this.currentVariant = this.initialVariant();
    this.render();
  }

  initialVariant() {
    if (this.querySelector('[data-option-input]')) {
      const fromOptions = this.variantFromOptions();
      /* Checked options win (scroll-race fix); hidden id only when they
         resolve to no variant (kit-form truncates option values at 2). */
      if (fromOptions) return fromOptions;
    }
    const id = this.idInput ? parseInt(this.idInput.value, 10) : NaN;
    return (
      this.product.variants.find((v) => v.id === id) ||
      this.product.variants.find((v) => v.available) ||
      this.product.variants[0] ||
      null
    );
  }

  variantFromOptions() {
    const selected = this.selectedOptions();
    return (
      this.product.variants.find((v) =>
        (v.options || []).every((value, i) => value === selected[i])
      ) || null
    );
  }

  selectedOptions() {
    const groups = new Map();
    for (const input of this.querySelectorAll('[data-option-input]')) {
      const key = input.dataset.optionName || input.name || 'option';
      if (!groups.has(key)) {
        groups.set(key, {
          position: parseInt(input.dataset.optionPosition, 10) || groups.size + 1,
          value: null,
        });
      }
      const group = groups.get(key);
      if (input.tagName === 'SELECT') group.value = input.value;
      else if (input.checked) group.value = input.value;
    }
    return [...groups.values()].sort((a, b) => a.position - b.position).map((g) => g.value);
  }

  selectedPlanId() {
    const checked = this.querySelector('[data-selling-plan]:checked');
    return checked ? checked.value : '';
  }

  onOptionChange() {
    this.currentVariant = this.variantFromOptions();
    this.render();
    this.emitChange();
  }

  emitChange() {
    const planId = this.selectedPlanId();
    const { price, compare } = this.priceFor(this.currentVariant, planId);
    bus.emit('variant:changed', {
      sectionId: this.dataset.sectionId,
      productId: this.product.id,
      variant: this.currentVariant,
      planId: planId ? Number(planId) : null,
      unitPrice: price,
      comparePrice: compare || null,
      available: !!this.currentVariant?.available,
    });
  }

  allocationFor(variant, planId) {
    if (!variant || !planId) return null;
    return (
      (variant.selling_plan_allocations || []).find(
        (a) => String(a.selling_plan_id ?? a.selling_plan?.id) === planId
      ) || null
    );
  }

  priceFor(variant, planId = this.selectedPlanId()) {
    if (!variant) return { price: 0, compare: 0 };
    const allocation = this.allocationFor(variant, planId);
    if (allocation) {
      return {
        price: allocation.price,
        compare: allocation.compare_at_price || variant.compare_at_price || 0,
      };
    }
    return { price: variant.price, compare: variant.compare_at_price || 0 };
  }

  syncPlans() {
    const radios = this.querySelectorAll('[data-selling-plan]');
    if (!radios.length) return;
    let fallback = null;
    let valid = false;
    for (const radio of radios) {
      const ok = !radio.value || !!this.allocationFor(this.currentVariant, radio.value);
      radio.closest('label')?.toggleAttribute('hidden', !ok);
      if (ok && !fallback && (!radio.value || this.product.requires_selling_plan)) fallback = radio;
      if (ok && radio.checked) valid = true;
    }
    if (!valid && fallback) fallback.checked = true;
  }

  render() {
    this.syncPlans();
    const variant = this.currentVariant;
    const { price, compare } = this.priceFor(variant);

    for (const el of this.querySelectorAll('[data-price]')) {
      el.textContent = variant ? formatMoney(price) : '';
    }
    const showCompare = !!variant && compare > price;
    for (const el of this.querySelectorAll('[data-compare-price]')) {
      el.textContent = showCompare ? formatMoney(compare) : '';
      el.toggleAttribute('hidden', !showCompare);
    }
    for (const el of this.querySelectorAll('[data-plan-price]:not(input)')) {
      el.textContent = variant
        ? formatMoney(this.priceFor(variant, el.dataset.planId || '').price)
        : '';
    }
    for (const el of this.querySelectorAll('[data-plan-compare]')) { // #282, rule in skio-plan-picker
      const p = variant ? this.priceFor(variant, el.dataset.planId || '').price : 0;
      const c = !variant ? 0 : p < variant.price ? variant.price : variant.compare_at_price || 0;
      el.textContent = c > p ? formatMoney(c) : '';
      el.hidden = !(c > p);
    }
    for (const el of this.querySelectorAll('[data-selected-option]')) {
      const value = variant && variant.options ? variant.options[parseInt(el.dataset.selectedOption, 10) - 1] : null;
      if (value != null) el.textContent = value;
    }
    for (const el of this.querySelectorAll('[data-availability]')) {
      const text = !variant
        ? el.dataset.unavailable || ''
        : variant.available
          ? el.dataset.inStock || ''
          : el.dataset.soldOut || '';
      el.textContent = text;
      el.toggleAttribute('hidden', !text);
    }
    if (this.idInput && variant) this.idInput.value = variant.id;

    const submit = this.form ? this.form.querySelector('[type="submit"]') : null;
    if (submit) {
      submit.toggleAttribute('disabled', !variant || !variant.available);
      const label = submit.querySelector('[data-atc-text]');
      if (label) {
        const text = !variant
          ? label.dataset.unavailable
          : variant.available
            ? label.dataset.add
            : label.dataset.soldOut;
        if (text) label.textContent = text;
      }
    }
  }
}

if (!customElements.get('product-form-controller')) {
  customElements.define('product-form-controller', ProductFormController);
}
